import {
  Avatar,
  Box,
  Button,
  Center,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import React, { useEffect, useState } from "react";
import { z } from "zod";

import { useUpdateUserProfile } from "api/users/updateUserProfile";
import { User } from "types";
import { getInitials } from "utils/getInitials";
import { Check } from "tabler-icons-react";
import { useResetPassword } from "api/users/useResetPassword";
import { useAuth } from "api/auth";
import { useNavigate } from "react-router-dom";

type EditProfileFormProps = {
  user: User;
  onClose: () => void;
};

const editProfileSchema = z.object({
  email: z
    .string()
    .email({ message: "Invalid email address" })
    .min(1, { message: "Please enter your email address" }),
  oldPassword: z
    .string()
    .min(6, { message: "Password must contain at least 6 characters" }),
  newPassword: z
    .string()
    .min(6, { message: "Password must contain at least 6 characters" }),
});

function ResetPasswordForm({ user, onClose }: EditProfileFormProps) {
  const { mutate: resetPassword } = useResetPassword();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const form = useForm({
    initialValues: {
      email: user.isDemo ? "guest@gmail.com" : user.email,
      oldPassword: "",
      newPassword: "",
    },
    validate: zodResolver(editProfileSchema),
  });
  const [initials, setInitials] = useState(
    getInitials(user.firstName, user.lastName)
  );
  const updateUserProfileMutation = useUpdateUserProfile();

  useEffect(() => {
    setInitials(getInitials(user.firstName, user.lastName));
  }, [user.firstName, user.lastName]);

  type FormValues = typeof form.values;

  const handleSubmit = (values: FormValues) => {
    resetPassword({
      email: values.email,
      oldPassword: values.oldPassword,
      newPassword: values.newPassword,
    });
    onClose();

    // logout();
    navigate("/login");
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <Center>
          <Avatar color="violet.7" size={80} radius={40}>
            {initials}
          </Avatar>
        </Center>
        <Text
          size={12}
          color="teal.9"
          mt={5}
          sx={{ display: "flex", alignItems: "center" }}
        >
          <Check size={14} />
          <Box ml={5}>After Update your session will be ended</Box>
        </Text>
        <TextInput
          label="Email"
          withAsterisk
          readOnly
          disabled
          {...form.getInputProps("email")}
        />
        <PasswordInput
          label="Old Password"
          description={
            form.errors.oldPassword || form.isValid("oldPassword")
              ? null
              : "Password must contain at least 6 characters"
          }
          inputWrapperOrder={["label", "input", "description", "error"]}
          styles={{
            error: {
              marginTop: 5,
            },
          }}
          {...form.getInputProps("oldPassword")}
        />
        {form.isValid("oldPassword") && (
          <Text
            size={12}
            color="teal.9"
            mt={5}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Check size={14} />
            <Box ml={5}>Password must contain at least 6 characters</Box>
          </Text>
        )}

        <PasswordInput
          label="New Password"
          description={
            form.errors.newPassword || form.isValid("newPassword")
              ? null
              : "Password must contain at least 6 characters"
          }
          inputWrapperOrder={["label", "input", "description", "error"]}
          styles={{
            error: {
              marginTop: 5,
            },
          }}
          {...form.getInputProps("newPassword")}
        />
        {form.isValid("newPassword") && (
          <Text
            size={12}
            color="teal.9"
            mt={5}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Check size={14} />
            <Box ml={5}>Password must contain at least 6 characters</Box>
          </Text>
        )}
        <Group position="right" spacing={20} mt={10}>
          <Button color="cancel" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="filled"
            disabled={!form.isDirty()}
            loading={updateUserProfileMutation.isLoading}
          >
            Update
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

export default ResetPasswordForm;
